import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の身だしなみ指針（route=/standards/grooming）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler/become や /standards/hospitality と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 設立1890年（明治23年）/ 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・違反件数・罰則は書かない

export default function StandardsGroomingPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/grooming']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の身だしなみ指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const hairFaceChecks = [
    {
      en: 'Hairline',
      ja: '髪の輪郭',
      text: '前髪・もみあげ・襟足が場の光で散らからないよう、出勤前に形を整えます。奇抜な変化より、依頼者の視線を奪わない安定した輪郭を優先します。'
    },
    {
      en: 'Skin & Beard',
      ja: '肌と髭',
      text: '髭を蓄える場合も、放置ではなく意図のある手入れとして保ちます。肌の荒れや寝癖の痕跡は、朝の短い点検で静かに取り除きます。'
    },
    {
      en: 'Expression',
      ja: '表情の余白',
      text: '過度な整髪料の光沢や強い香り付き製品は避け、顔まわりが「主張」ではなく「背景」になる状態を目指します。'
    }
  ];

  const handNailPoints = [
    {
      no: '01',
      title: '清潔が先、装飾は後',
      text: '給仕・扉・書類に直接触れる手は、色や装飾より清潔と長さの管理が本体です。爪は短く整え、縁に汚れが残らないことを毎回確認します。'
    },
    {
      no: '02',
      title: '触れる前の一呼吸',
      text: '食器や布巾、依頼者の私物に触れる直前に、手の状態を意識する習慣を持ちます。洗った直後の水気や、保湿のべたつきも品位を損ねます。'
    },
    {
      no: '03',
      title: '目立たない手入れ',
      text: 'ささくれや荒れは、奉仕の最中ではなく控室や休憩のあいだに処置します。現場で目立つケア道具を広げないことが、職能としての節度です。'
    },
    {
      no: '04',
      title: '共有物への配慮',
      text: '手袋や共用の布を扱う前後でも、手の状態を整えます。自分の手が次の人の道具を汚さない——その意識が相互信頼の土台になります。'
    }
  ];

  const attireCare = [
    {
      label: '生地とシルエット',
      lines: [
        '皺・毛玉・糸のほつれを出勤前に点検する',
        '肩・袖口・裾のラインが崩れていないか鏡で確認する',
        '季節と室内温度に合わせ、重ね着の厚みを調整する'
      ]
    },
    {
      label: '白と金属の管理',
      lines: [
        'カフス・襟・ポケットチーフの白さを保つ',
        'ボタン・カフリンクスの曇りや緩みをその日のうちに直す',
        '靴の輝きは過剰な鏡面より、手入れされた落ち着きを目指す'
      ]
    },
    {
      label: '交換と保管',
      lines: [
        '一日の終わりに着用物を分け、通気のある場所へ',
        '予備のシャツや靴下を現場の動線から外れた位置に備える',
        '香りや湿気が移らないよう、他の衣類と距離を取る'
      ]
    }
  ];

  const scentReasons = [
    {
      en: 'Neutral',
      ja: '中立であること',
      text: '香水や強い整髪料は、依頼者や来客の好みと衝突しやすいものです。身だしなみの香りは「何も主張しない清潔さ」が基本になります。'
    },
    {
      en: 'Space',
      ja: '空間を占有しない',
      text: '狭い廊下や車内、食卓のそばでは、わずかな香りでも場を占有します。残香が記憶に残るほど、奉仕者の存在が前景に出てしまいます。'
    },
    {
      en: 'Food',
      ja: '食の場を守る',
      text: '給仕では、手と袖口に残る香りが料理の印象を歪めます。無香に近い石けんと、十分なすすぎを日常の型とします。'
    }
  ];

  const dressByContext = [
    {
      en: 'Morning',
      ja: '朝〜日中の実務',
      text: '動きやすさと視認性の安定を優先し、光沢や装飾を抑えた装いを選びます。屋内外を往復する場合は、上着の着脱が自然に見える層構成にします。'
    },
    {
      en: 'Formal',
      ja: '改まった時間帯',
      text: '夕食・式典・公式な来訪では、家の流儀に沿った格式の段を上げます。自分の好みで華美にするのではなく、場が求める静けさに合わせます。'
    },
    {
      en: 'Transit',
      ja: '移動と待機',
      text: '外出同行や車中待機でも、室内と同じ基準の延長で整えます。上着を脱いだ姿が「崩れた私服」に見えないよう、下層の衣類まで点検します。'
    },
    {
      en: 'Private',
      ja: '邸内の静かな時間',
      text: '依頼者がくつろぐ時間帯は、音と光沢をさらに下げます。装いが会話や休息の邪魔をしないこと——それが格式のもう一つの形です。'
    }
  ];

  const mutualInspection = [
    '鏡だけでなく、同僚の短い一言を点検の一部として受け取る',
    '指摘は人格ではなく、襟・袖・靴・髪など具体箇所に限定する',
    '出勤前の数分を「相互確認の枠」として確保し、場当たりにしない',
    '直せることはその場で直し、時間を要することは交代前に共有する',
    '上位資格者の示范を観察し、自分の盲点を更新する'
  ];

  const faqItems = [
    {
      q: '香水を一切用いないのは、個性を否定するためですか？',
      a: 'いいえ。個性は奉仕の精度や判断の丁寧さに表れるものであり、香りで主張する必要はない、という考え方です。依頼者・来客・料理・空間のどれにも干渉しない中立さが、身だしなみにおける品位だと考えています。'
    },
    {
      q: '手と爪の基準は、なぜこれほど重視されるのですか？',
      a: '執事の手は給仕・文書・扉・衣類など、依頼者の生活動線に直接触れます。顔や上着が整っていても、爪の長さや手の荒れは至近距離で必ず目に入ります。だからこそ、装飾より清潔と長さの管理を本体としています。'
    },
    {
      q: '相互点検は、上下関係を強くするものではありませんか？',
      a: '相互点検の目的は序列の確認ではなく、一人では見えにくい箇所を補い合うことです。具体的な箇所だけを短く伝え、直したら閉じる——その型を守れば、人格批判ではなく職能の品質管理として機能します。AssociateからMasterまで、同じ目で整え合う習慣が現場の再現性を支えます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の身だしなみ指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の身だしなみ指針。基準化の理由、髪と顔まわり、手と爪、装いの清潔と手入れ、匂いの管理、時間帯と格式による装いの選択、相互点検の習慣。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/grooming'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の身だしなみ指針', item: 'https://japanbutlerassociation.com/standards/grooming' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-grooming-01.jpg"
          alt="整った装いで静かに立つ執事。身だしなみの品位を象徴する一枚"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Grooming Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の身だしなみ指針</span>
              <span className="block text-[#d7b779]">装いと清潔の基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創設以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める身だしなみの基準として、髪・顔・手・装い・匂い・格式に応じた選択、そして相互点検の習慣を示します。身だしなみは自己表現の場ではなく、依頼者が安心して場を委ねるための静かな基盤です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化の理由 — 引用帯＋二段本文＋二分割リンク（hospitalityの三指標グリッドとは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. Why Standards
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              身だしなみを<span className="text-[#a38e65]">基準化する理由</span>
            </h2>
            <div className="border-l-2 border-[#a38e65] bg-[#fbfaf7] px-6 sm:px-8 py-6 mb-8">
              <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">
                Appearance is the first silence.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                言葉を発する前に、姿が場へ届きます。その最初の沈黙が安心を生むか、違和を残すか——身だしなみの基準は、個人の好みを揃えるためではなく、奉仕の入口を誰にとっても同じ高さに保つためにあります。
              </p>
            </div>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                当協会は、Associate・Professional・Masterの三段階と専門資格を通じて技能を育てると同時に、現場で迷わない身だしなみの座標を本指針にまとめています。髪型の流行や装いの流行を追うことが目的ではありません。依頼者の視線を奪わず、来客に家の品位を過不足なく伝え、給仕や取次の動作を妨げない状態を、再現可能な型として共有することです。
              </p>
              <p>
                立ち居振る舞いの細部は
                <a href="/standards/deportment" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">立ち居振る舞いの基準</a>
                と、日々の心得は
                <a href="/standards/conduct" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事の行動規範</a>
                と併せて参照し、基準全体の見取り図は
                <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                から俯瞰できます。身だしなみは単体の美意識ではなく、職能全体を支える下地です。
              </p>
            </div>
          </section>

          {/* S2. 髪と顔 — 上に横長画像、下に三枚の細カード（左文右図の反転構成） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Hair & Face
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              髪と顔まわり
            </h2>
            <img
              src="/images/generated/jba-std-grooming-02.jpg"
              alt="礼節研修のなかで姿勢と顔まわりの整え方を確認する様子"
              className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30 mb-8"
            />
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              顔は常に相手の視界に入ります。だからこそ、髪と顔まわりは「整っていること」より「忘れられること」を目指します。光を反射しすぎる整髪、顔に落ちる前髪、手入れの行き届かない髭は、会話の内容より先に印象を占有してしまいます。朝の短い儀式として、輪郭・肌・表情の余白を順に確認します。
            </p>
            <div className="grid sm:grid-cols-3 gap-px bg-[#1a2942]">
              {hairFaceChecks.map((item) => (
                <div key={item.en} className="bg-white px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{item.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S3. 手と爪 — 紺ヘッドバンド＋番号細罫（給仕直結を強調） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Hands & Nails
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              手と爪——給仕に直結する基準
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              身だしなみのなかでも、手と爪は給仕の品質に直結します。器の縁、グラスの胴、手紙の封、椅子の背——依頼者と来客の生活に触れるすべての動作の手前に、手があります。美しい上着も、手元が乱れていれば奉仕の印象はそこで途切れます。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-0 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">HAND SERVICE</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                触れる手が、最初の接遇になる
              </span>
            </div>
            <div className="border border-t-0 border-[#e2ddd2] bg-[#fbfaf7]">
              {handNailPoints.map((item) => (
                <div key={item.no} className="flex gap-5 px-6 sm:px-8 py-5 border-b border-[#e2ddd2] last:border-b-0">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">{item.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-8 items-center">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                爪の長さは「引っかからない・音が立たない・汚れが残らない」を目安にします。塗色や装飾で個性を出す必要はありません。保湿は必要な範囲で行い、べたつきが器や紙に移らないことを確認してから場へ戻ります。手の基準は、見た目の美しさであると同時に、衛生と静音の基準でもあります。
              </p>
              <img
                src="/images/generated/jba-std-grooming-03.jpg"
                alt="手元の清潔と仕上がりを点検する執事の手"
                className="w-full h-48 lg:h-56 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. 装いの清潔 — 三列縦カード＋下帯メモ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Attire & Care
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              装いの清潔と手入れ
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                装いの品位は、新品であることで測るのではありません。皺・埃・糸のほつれ・靴のくすみといった小さな乱れを、その日のうちに戻せるかどうかで測ります。清潔は一度整えれば終わりではなく、移動・作業・天候のあとに更新し続ける行為です。
              </p>
              <p>
                家の制服規定や色の指定がある場合は、それに従うことが第一です。指定が緩やかな環境でも、光沢・柄・香りの主張を抑え、動作の邪魔にならないシルエットを選びます。装いは自己展示ではなく、場の背景になるための道具です。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {attireCare.map((block) => (
                <div key={block.label} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#e2ddd2]">{block.label}</h3>
                  <ul className="space-y-3">
                    {block.lines.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <img
              src="/images/generated/jba-std-grooming-04.jpg"
              alt="装いと動線を現場で確認しながら奉仕の準備をする執事"
              className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S5. 匂いの管理 — 中央リード＋三分割理由（香水を用いない） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Scent Discipline
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              匂いの管理——香水を用いない理由
            </h2>
            <div className="bg-[#1a2942] p-8 sm:p-10 mb-8">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                当協会の身だしなみ指針では、香水や強い香りの整髪料・ボディ製品を用いません。香りは好みが分かれやすく、一度空間に広がると取り消せないからです。身だしなみにおける最善の香りは、清潔そのもの——洗い流したあとに何も残らない状態です。
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {scentReasons.map((r) => (
                <div key={r.en} className="border-t-2 border-[#1a2942] pt-5">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{r.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{r.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-start">
              <img
                src="/images/generated/jba-std-grooming-05.jpg"
                alt="出勤前に身だしなみと香りの有無を静かに確認するチーム"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  衣類に残る柔軟剤や、控室の芳香剤の移り香にも注意します。自分では慣れて感じなくても、食卓や書斎では強く届くことがあります。洗濯・保管・着替えの各段階で「無香に近づける」選択を重ねることが、匂いの管理の実務です。
                </p>
                <p>
                  もし体調管理や医療上の理由で特定の外用が必要な場合は、香りの少ない製品を選び、塗布後に十分乾かしてから場へ入ります。原則は守りつつ、安全と誠実さを優先する判断が職能に求められます。
                </p>
              </div>
            </div>
          </section>

          {/* S6. 時間帯と格式 — 横四分割ステップ（装いの選択） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Time & Register
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              時間帯と格式による装いの選択
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              同じ邸内でも、朝の実務と夜の改まった時間では、装いに求める層が変わります。変化の目的は着飾ることではなく、場の呼吸に合わせた可読性を保つことです。家ごとの規定を最上段に置きつつ、時間帯と来客の有無で静かに段差をつけます。
            </p>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-2 border-[#1a2942] mb-10">
              {dressByContext.map((s) => (
                <li key={s.en} className="border-b sm:border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-3">{s.ja}</span>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                </li>
              ))}
            </ol>
            <div className="bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                格式を上げるときは、アクセサリーを増やすより、生地の清潔・線の正確さ・靴の手入れを一段丁寧にします。下げすぎて私的なくつろぎ着に見えることも避けます。依頼者が求める空気を観察し、装いの層を更新する——その判断力は、資格の段階を問わず現場で磨かれます。
              </p>
            </div>
          </section>

          {/* S7. 相互点検 — 画像右＋リスト左の紺パネル */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Mutual Inspection
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              相互点検の習慣
            </h2>
            <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12">
              <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8">
                    身だしなみの盲点は、本人の鏡だけでは抜けきれません。襟の折り返し、後頭部の髪、靴の踵、袖口の糸——相互点検は監視ではなく、品質を分かち合う短い儀式です。指摘する側もされる側も、人格ではなく箇所を扱い、直したら感謝で閉じます。
                  </p>
                  <ul className="space-y-4">
                    {mutualInspection.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0">—</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <img
                  src="/images/generated/jba-std-grooming-06.jpg"
                  alt="落ち着いた佇まいで相互点検の示范を示す上級執事"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/40"
                />
              </div>
            </div>
            <p className="mt-8 font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              Master Butlerが示す統括の視点も、こうした日常の信号をチームの型へ戻すところにあります。相互点検が定着すると、出勤前の焦りが減り、依頼者の前で慌てて直す場面が静かに消えていきます。習慣は規則の代替ではなく、規則を生きたものに保つための呼吸です。
            </p>
          </section>

          {/* S8. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問い
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S9. 関連導線 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IX. Related Paths
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h3>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                身だしなみ指針は、単独で完結するものではありません。立ち居振る舞いと行動の基準で身体と判断の型を整え、資格制度を通じて段階的に再現性を育てることで、現場の品位が続きます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/deportment" className={BTN08_ON_NAVY}>
                  立ち居振る舞いの基準
                </a>
                <a href="/standards/conduct" className={BTN08_ON_NAVY}>
                  執事の行動規範
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
